import { useState } from 'react'
import { FileWarning, Sparkles, X } from 'lucide-react'
import { FileUploader, FormDialog, type UploadedFile } from '@/components/common'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Spinner } from '@/components/ui/spinner'
import { toApiProblem } from '@/lib/api/client'
import { useAiSettings, useExtractionMutations } from '../api'
import { confidenceLabel, confidenceTone, EXTRACTED_FIELDS } from '../types'
import type { ProposalView } from '../types'
import type { DocumentTypeView } from '@/features/document'
import { Select } from '@/components/ui/select'
import { DatePicker } from '@/components/ui/date-picker'

interface ExtractionDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  employeeId: string
  types: DocumentTypeView[]
}

/** The document's own fields, as the confirm step will save them. */
interface Confirmed {
  documentNumber: string
  issuingCountry: string
  issuingAuthority: string
  issueDate: string
  expiryDate: string
  notes: string
}

const EMPTY: Confirmed = {
  documentNumber: '',
  issuingCountry: '',
  issuingAuthority: '',
  issueDate: '',
  expiryDate: '',
  notes: '',
}

/**
 * A proposed value as a string.
 *
 * <p>The proposal is jsonb, so a provider could put anything in it. Only primitives are
 * rendered — an object would otherwise reach the screen as "[object Object]" and, worse,
 * be saved as that if somebody pressed Confirm without reading it.
 */
function text(value: unknown): string {
  if (value == null) return ''
  if (typeof value === 'string') return value
  if (typeof value === 'number' || typeof value === 'boolean') return String(value)
  return ''
}

function numberOrNull(value: unknown): number | null {
  return typeof value === 'number' ? value : null
}

/**
 * Upload a scan, look at what was read, correct it, save it.
 *
 * <p>The document and the proposed values are side by side and stay that way. The entire
 * point of a human confirmation step is that somebody compares the two, and a flow that
 * shows the fields on a screen after the image has gone is a flow where people press
 * Confirm without looking — which is worse than typing the values by hand, because it
 * carries the appearance of having been checked.
 *
 * <p>Nothing here writes a document. Confirm posts to the confirm endpoint, which saves
 * through the ordinary document path with every rule that applies to a hand-typed one.
 */
export function ExtractionDialog({ open, onOpenChange, employeeId, types }: ExtractionDialogProps) {
  const settings = useAiSettings(open)
  const { extract, confirm, reject } = useExtractionMutations(employeeId)

  const [documentTypeId, setDocumentTypeId] = useState('')
  const [file, setFile] = useState<UploadedFile | null>(null)
  const [proposal, setProposal] = useState<ProposalView | null>(null)
  const [values, setValues] = useState<Confirmed>(EMPTY)
  const [error, setError] = useState<string | null>(null)

  const current = settings.data
  const allowedTypes = types.filter((type) => current?.allowedDocumentTypes?.includes(type.id))

  /** Why extraction cannot be used right now, in a sentence, or null when it can. */
  const unavailable = (() => {
    if (settings.isLoading) return null
    if (!current?.configured) {
      return 'Document extraction has not been set up for this company. An administrator turns it on in Settings.'
    }
    if (!current.enabled) {
      return 'Document extraction is switched off for this company.'
    }
    if (current.provider === 'OCR_MRZ' && !current.ocrAvailable) {
      return current.ocrUnavailableReason
        ?? 'The on-device reader cannot run on this server, so nothing can be extracted.'
    }
    if (allowedTypes.length === 0) {
      return 'No document type has been enrolled for extraction. An administrator chooses which types may be read.'
    }
    return null
  })()

  function reset() {
    setDocumentTypeId('')
    setFile(null)
    setProposal(null)
    setValues(EMPTY)
    setError(null)
  }

  function close(next: boolean) {
    if (!next) reset()
    onOpenChange(next)
  }

  async function runExtraction() {
    if (!file || !documentTypeId) return
    setError(null)
    try {
      const result = await extract.mutateAsync({
        storedFileId: file.id,
        documentTypeId,
        employeeId,
      })
      setProposal(result)
      // Prefilled from the proposal, and editable. issuingState is the document's own word
      // for the country that issued it, which is what issuingCountry records.
      setValues({
        documentNumber: text(result.proposal.documentNumber),
        issuingCountry: text(result.proposal.issuingState).slice(0, 2),
        issuingAuthority: '',
        issueDate: text(result.proposal.issueDate),
        expiryDate: text(result.proposal.expiryDate),
        notes: '',
      })
    } catch (problem) {
      setError(toApiProblem(problem)?.detail ?? 'The document could not be read')
    }
  }

  async function onConfirm() {
    if (!proposal) return
    setError(null)
    try {
      await confirm.mutateAsync({
        proposalId: proposal.id,
        employeeId,
        documentTypeId,
        documentNumber: values.documentNumber || null,
        issuingCountry: values.issuingCountry || null,
        issuingAuthority: values.issuingAuthority || null,
        issueDate: values.issueDate || null,
        expiryDate: values.expiryDate || null,
        storedFileId: file?.id ?? null,
        notes: values.notes || null,
      })
      close(false)
    } catch (problem) {
      setError(toApiProblem(problem)?.detail ?? 'The document could not be read')
    }
  }

  async function onReject() {
    if (!proposal) return
    try {
      await reject.mutateAsync(proposal.id)
      close(false)
    } catch (problem) {
      setError(toApiProblem(problem)?.detail ?? 'The document could not be read')
    }
  }

  const isPdf = file?.contentType === 'application/pdf'
  const fileUrl = file ? `/api/v1/files/${file.id}` : null

  return (
    <FormDialog
      open={open}
      onOpenChange={close}
      size="lg"
      title="Read a document"
      description="Nothing is saved until you confirm it."
      submitLabel={proposal ? 'Confirm and save' : 'Read the document'}
      loading={extract.isPending || confirm.isPending}
      error={error}
      onSubmit={() => void (proposal ? onConfirm() : runExtraction())}
    >
      {unavailable ? (
        <div
          role="alert"
          data-testid="extraction-unavailable"
          className="flex items-start gap-2 rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900"
        >
          <FileWarning className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
          <p>{unavailable}</p>
        </div>
      ) : (
        <div className="space-y-4">
          {!proposal && (
            <>
              <div>
                <Label htmlFor="extract-type">Document type</Label>
                <Select
                  id="extract-type"
                  className="w-full"
                  value={documentTypeId}
                  onChange={(event) => setDocumentTypeId(event.target.value)}
                >
                  <option value="">Choose a type…</option>
                  {allowedTypes.map((type) => (
                    <option key={type.id} value={type.id}>{type.name}</option>
                  ))}
                </Select>
              </div>

              <div>
                <Label>The scan</Label>
                <FileUploader
                  ownerType="EMPLOYEE"
                  ownerId={employeeId}
                  files={file ? [file] : []}
                  accept="image/jpeg,image/png,application/pdf"
                  onUploaded={setFile}
                  onRemoved={() => setFile(null)}
                />
              </div>

              {extract.isPending && (
                <p className="flex items-center gap-2 text-sm text-slate-600">
                  <Spinner className="h-4 w-4" /> Reading the document…
                </p>
              )}
            </>
          )}

          {proposal && (
            <div className="grid gap-4 lg:grid-cols-2">
              {/* The document, kept in view while the values beside it are checked. */}
              <div className="rounded-md border border-slate-200 bg-slate-50 p-2">
                {fileUrl && !isPdf && (
                  <img src={fileUrl} alt="The uploaded document" className="max-h-[28rem] w-full object-contain" />
                )}
                {fileUrl && isPdf && (
                  <object data={fileUrl} type="application/pdf" className="h-[28rem] w-full">
                    <p className="p-3 text-sm text-slate-600">
                      This browser will not display the PDF inline.{' '}
                      <a className="underline" href={fileUrl} target="_blank" rel="noreferrer">
                        Open it in a new tab
                      </a>{' '}
                      to check the values against it.
                    </p>
                  </object>
                )}
                <p className="mt-2 px-1 text-xs text-slate-600">
                  Read by {proposal.provider}
                  {proposal.modelVersion ? ` (${proposal.modelVersion})` : ''}
                  {proposal.latencyMs != null ? ` in ${proposal.latencyMs} ms` : ''}.
                </p>
              </div>

              <div className="space-y-3">
                {proposal.warnings && (
                  <p role="alert" className="rounded-md border border-amber-300 bg-amber-50 p-2 text-sm text-amber-900">
                    {proposal.warnings}
                  </p>
                )}

                <div>
                  <h3 className="mb-1 text-sm font-medium">What was read</h3>
                  <dl className="divide-y divide-slate-100 rounded-md border border-slate-200">
                    {EXTRACTED_FIELDS.map(({ key, label }) => {
                      const value = text(proposal.proposal[key])
                      if (!value) return null
                      const score = numberOrNull(proposal.confidence[key])
                      const tone = confidenceTone(score)
                      return (
                        <div key={key} className="flex items-baseline justify-between gap-3 px-3 py-1.5">
                          <dt className="text-sm text-slate-600">{label}</dt>
                          <dd className="text-end">
                            <span className="block text-sm font-medium">{value}</span>
                            <span
                              className={
                                tone === 'good' ? 'text-xs text-emerald-700'
                                  : tone === 'warn' ? 'text-xs text-amber-700'
                                    : 'text-xs text-red-700'
                              }
                            >
                              {confidenceLabel(score, proposal.provider)}
                            </span>
                          </dd>
                        </div>
                      )
                    })}
                  </dl>
                  {Object.keys(proposal.proposal).length === 0 && (
                    <p className="text-sm text-slate-600">
                      Nothing was read from this document. Fill the fields in by hand, or reject
                      the proposal and type the document in the ordinary way.
                    </p>
                  )}
                </div>

                <div className="space-y-2">
                  <h3 className="text-sm font-medium">What will be saved</h3>
                  <p className="text-sm text-slate-600">
                    Correct anything that is wrong. Saving values you changed is recorded as an
                    edit, which is how a provider that is not working becomes visible.
                  </p>
                  <div className="grid gap-2 sm:grid-cols-2">
                    <div>
                      <Label htmlFor="confirm-number">Document number</Label>
                      <Input
                        id="confirm-number"
                        value={values.documentNumber}
                        onChange={(event) => setValues({ ...values, documentNumber: event.target.value })}
                      />
                    </div>
                    <div>
                      <Label htmlFor="confirm-country">Issuing country</Label>
                      <Input
                        id="confirm-country"
                        maxLength={2}
                        value={values.issuingCountry}
                        onChange={(event) =>
                          setValues({ ...values, issuingCountry: event.target.value.toUpperCase() })}
                      />
                    </div>
                    <div>
                      <Label htmlFor="confirm-authority">Issuing authority</Label>
                      <Input
                        id="confirm-authority"
                        value={values.issuingAuthority}
                        onChange={(event) => setValues({ ...values, issuingAuthority: event.target.value })}
                      />
                    </div>
                    <div>
                      <Label htmlFor="confirm-issue">Issued</Label>
                      <DatePicker
                        id="confirm-issue"
                        value={values.issueDate}
                        onChange={(event) => setValues({ ...values, issueDate: event.target.value })}
                      />
                    </div>
                    <div>
                      <Label htmlFor="confirm-expiry">Expires</Label>
                      <DatePicker
                        id="confirm-expiry"
                        value={values.expiryDate}
                        onChange={(event) => setValues({ ...values, expiryDate: event.target.value })}
                      />
                    </div>
                    <div>
                      <Label htmlFor="confirm-notes">Notes</Label>
                      <Input
                        id="confirm-notes"
                        value={values.notes}
                        onChange={(event) => setValues({ ...values, notes: event.target.value })}
                      />
                    </div>
                  </div>
                </div>

                <Button
                  type="button"
                  variant="outline"
                  onClick={() => void onReject()}
                  disabled={reject.isPending}
                >
                  <X className="me-2 h-4 w-4" aria-hidden />
                  Reject this proposal
                </Button>
                <p className="text-xs text-slate-600">
                  Rejecting saves nothing. The record that a proposal was made and refused is
                  kept either way.
                </p>
              </div>
            </div>
          )}

          {!proposal && (
            <p className="flex items-start gap-2 text-sm text-slate-600">
              <Sparkles className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
              The document is read and its values proposed. You compare them against the scan
              and decide what is saved.
            </p>
          )}
        </div>
      )}
    </FormDialog>
  )
}
